import moment from "moment";
import Modal from "react-modal";
import { connect } from "react-redux";
import { capitalize, get, isEmpty } from "lodash";
import { Actions } from "../../internals/app/Actions";
import { formatPrice } from "../../internals/managers/PaymentManager";

// ─── Modal styles ─────────────────────────────────────────────────────────────

const customStyles: any = {
  overlay: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
    display: "flex",
    position: "fixed",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(15, 15, 26, 0.6)",
    backdropFilter: "blur(4px)",
    padding: "16px",
  },
  content: {
    padding: 0,
    border: "none",
    background: "none",
    inset: "unset",
    position: "relative",
    overflow: "visible",
    width: "100%",
    maxWidth: "480px",
    maxHeight: "90vh",
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const S3_BASE =
  "https://netlise-image-bucket.s3.ap-southeast-1.amazonaws.com/public/";

const shopLogoUrl = (image?: string) =>
  image ? `${S3_BASE}${image}` : null;

// ─── Sub-components ───────────────────────────────────────────────────────────

const Divider = ({ dashed = false }: { dashed?: boolean }) => (
  <div
    className={`w-full my-3 ${
      dashed
        ? "border-t border-dashed border-echo-blue/50"
        : "border-t border-lavender"
    }`}
  />
);

const LineItem = ({
  label,
  value,
  bold = false,
  accent = false,
  muted = false,
  large = false,
}: {
  label: string;
  value: string;
  bold?: boolean;
  accent?: boolean;
  muted?: boolean;
  large?: boolean;
}) => (
  <div className="flex items-center justify-between gap-4">
    <span
      className={`${large ? "text-sm" : "text-xs"} ${
        bold ? "font-semibold" : "font-normal"
      } ${
        muted
          ? "text-echo-blue"
          : accent
          ? "text-medium-slate-blue"
          : "text-gulf-blue"
      }`}
    >
      {label}
    </span>
    <span
      className={`font-mono shrink-0 ${large ? "text-sm" : "text-xs"} ${
        bold ? "font-semibold" : "font-normal"
      } ${
        muted
          ? "text-echo-blue"
          : accent
          ? "text-medium-slate-blue"
          : "text-midnight-express"
      }`}
    >
      {value}
    </span>
  </div>
);

/** Normalise any stored variant to one of: 'cash' | 'card' | 'split' */
const normalizePaymentKey = (method: string): 'cash' | 'card' | 'split' => {
  const m = (method || '').toLowerCase().replace(/[_\s-]/g, '');
  if (m === 'card' || m === 'creditcard' || m === 'credit') return 'card';
  if (m === 'split') return 'split';
  return 'cash';
};

const PaymentBadge = ({ method }: { method: string }) => {
  const key = normalizePaymentKey(method);
  const map: Record<string, { style: string; icon: string; label: string }> = {
    cash: {
      style: "bg-emerald-50 text-emerald-700 border-emerald-200",
      icon: "💵",
      label: "Cash",
    },
    card: {
      style: "bg-blue-50 text-blue-700 border-blue-200",
      icon: "💳",
      label: "Card",
    },
    split: {
      style:
        "bg-medium-slate-blue-16 text-medium-slate-blue border-medium-slate-blue/20",
      icon: "⚡",
      label: "Split",
    },
  };
  const { style, icon, label } = map[key];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${style}`}
    >
      {icon} {label}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const InvoiceDetail = (props: any) => {
  const { isOpen, closeModal, transaction, selectedShop, onSendAsInvoice } = props;

  if (!transaction || isEmpty(transaction)) return null;

  // ── Totals ─────────────────────────────────────────────────────────────────

  const currency = selectedShop?.currency;
  const fmt = (n: number) => formatPrice(n, currency);

  const subTotalProducts = (transaction.productDetails ?? []).reduce(
    (s: number, p: any) => s + (p.totalPrice ?? 0),
    0
  );
  const subTotalServices = (transaction.serviceDetails ?? []).reduce(
    (s: number, sv: any) => s + (sv.totalPrice ?? 0),
    0
  );
  const subTotal = subTotalProducts + subTotalServices;

  const discountTotal = (transaction.discountDetails ?? []).reduce(
    (s: number, d: any) => s + (d.amount ?? 0),
    0
  );
  const serviceChargeAmount = parseFloat(
    get(transaction, "serviceCharge.amount", 0)
  );
  const grandTotal = subTotal + serviceChargeAmount - discountTotal;
  const paidAmount = transaction.paidPrice ?? grandTotal;
  const change = paidAmount - grandTotal;

  // ── Payment method ─────────────────────────────────────────────────────────

  const paymentMethod: string =
    transaction.paymentMethod ?? transaction.payment ?? "cash";
  const normalizedMethod = normalizePaymentKey(paymentMethod);
  const isSplit = normalizedMethod === "split";
  const isCard = normalizedMethod === "card";
  const cashPaid: number =
    transaction.cashPaid ?? (isSplit ? 0 : isCard ? 0 : paidAmount);
  const cardPaid: number =
    transaction.cardPaid ?? (isSplit ? 0 : isCard ? paidAmount : 0);

  // ── Employee & meta ────────────────────────────────────────────────────────

  const employeeName = `${capitalize(
    get(transaction, "employee.firstName", "")
  )} ${capitalize(
    get(transaction, "employee.lastName", "")?.charAt(0)
  )}`.trim();

  const formattedDate = transaction.createdTime
    ? moment(transaction.createdTime).format("DD MMM YYYY")
    : "—";
  const formattedTime = transaction.time ?? "—";

  // ── Shop logo ──────────────────────────────────────────────────────────────

  const logoUrl = shopLogoUrl(selectedShop?.image);
  const shopName: string = selectedShop?.shopName ?? "Receipt";

  const hasProducts = !isEmpty(transaction.productDetails);
  const hasServices = !isEmpty(transaction.serviceDetails);
  const hasDiscount = discountTotal > 0;
  const hasServiceCharge = serviceChargeAmount > 0;
  const hasChange = change > 0.009;

  return (
    <Modal
      isOpen={isOpen}
      style={customStyles}
      contentLabel="Receipt Detail"
      onRequestClose={closeModal}
      ariaHideApp={false}
    >
      <div className="flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden w-full">

        {/* ── Brand header ─────────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{
            background: "linear-gradient(135deg, #6D71F9 0%, #575AC7 100%)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={shopName}
                className="h-9 w-9 rounded-lg object-cover bg-white/20 shrink-0"
                onError={(e: any) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-white/20 shrink-0">
                <span className="text-white font-bold text-base">
                  {shopName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <span className="text-white font-semibold text-sm truncate">
              {shopName}
            </span>
          </div>

          <button
            onClick={closeModal}
            className="flex items-center justify-center w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 transition-colors shrink-0 ml-3"
          >
            <svg
              className="w-3.5 h-3.5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* ── Scrollable body ───────────────────────────────────────────── */}
        <div
          className="overflow-y-auto overflow-x-hidden"
          style={{ maxHeight: "calc(90vh - 68px)" }}
        >
          <div className="px-5 pt-4 pb-6">

            {/* ── Receipt meta ──────────────────────────────────────────── */}
            <div className="flex items-start justify-between gap-4 mb-1">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-echo-blue mb-0.5">
                  Receipt No.
                </p>
                <p className="font-mono text-sm font-semibold text-midnight-express truncate">
                  {transaction.recieptId ?? "—"}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-echo-blue mb-0.5">
                  Date &amp; Time
                </p>
                <p className="text-xs font-medium text-midnight-express">
                  {formattedDate}
                </p>
                <p className="text-xs text-echo-blue">{formattedTime}</p>
              </div>
            </div>

            {employeeName && (
              <div className="flex items-center gap-1.5 mt-2 mb-1">
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-medium-slate-blue-16 shrink-0">
                  <svg
                    className="w-3 h-3 text-medium-slate-blue"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <span className="text-xs text-gulf-blue">
                  Served by{" "}
                  <span className="font-semibold text-midnight-express">
                    {employeeName}
                  </span>
                </span>
              </div>
            )}

            <Divider />

            {/* ── Column headers ────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold uppercase tracking-widest text-echo-blue flex-1">
                Description
              </p>
              <div className="flex items-center shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-echo-blue w-8 text-center">
                  Qty
                </p>
                <p className="text-[10px] font-bold uppercase tracking-widest text-echo-blue w-20 text-right">
                  Amount
                </p>
              </div>
            </div>

            {/* ── Product items ──────────────────────────────────────────── */}
            {hasProducts && (
              <>
                {hasServices && (
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-medium-slate-blue mb-1.5">
                    Products
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  {transaction.productDetails.map((item: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-start justify-between gap-2"
                    >
                      <p className="text-xs text-gulf-blue flex-1 leading-snug min-w-0 pr-1">
                        {item.productName ?? item.name ?? "Item"}
                      </p>
                      <div className="flex items-center shrink-0">
                        <p className="text-xs text-midnight-express w-8 text-center">
                          {item.quantity ?? 1}
                        </p>
                        <p className="font-mono text-xs text-midnight-express w-20 text-right">
                          {fmt(item.totalPrice)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── Service items ──────────────────────────────────────────── */}
            {hasServices && (
              <>
                <p
                  className={`text-[10px] font-semibold uppercase tracking-wider text-medium-slate-blue mb-1.5 ${
                    hasProducts ? "mt-3" : ""
                  }`}
                >
                  Services
                </p>
                <div className="flex flex-col gap-2">
                  {transaction.serviceDetails.map((item: any, i: number) => (
                    <div
                      key={i}
                      className="flex items-start justify-between gap-2"
                    >
                      <p className="text-xs text-gulf-blue flex-1 leading-snug min-w-0 pr-1">
                        {item.serviceName ?? item.name ?? "Service"}
                      </p>
                      <div className="flex items-center shrink-0">
                        <p className="text-xs text-midnight-express w-8 text-center">
                          {item.quantity ?? 1}
                        </p>
                        <p className="font-mono text-xs text-midnight-express w-20 text-right">
                          {fmt(item.totalPrice)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <Divider dashed />

            {/* ── Totals ────────────────────────────────────────────────── */}
            <div className="flex flex-col gap-2">
              <LineItem label="Subtotal" value={fmt(subTotal)} />
              {hasDiscount && (
                <LineItem
                  label={`Discount${
                    transaction.discountDetails?.length > 1
                      ? ` (${transaction.discountDetails.length})`
                      : ""
                  }`}
                  value={`− ${fmt(discountTotal)}`}
                  muted
                />
              )}
              {hasServiceCharge && (
                <LineItem
                  label={`Service Charge${
                    transaction.serviceCharge?.percentage
                      ? ` (${transaction.serviceCharge.percentage}%)`
                      : ""
                  }`}
                  value={fmt(serviceChargeAmount)}
                  muted
                />
              )}
            </div>

            {/* ── Grand total highlight ──────────────────────────────────── */}
            <div
              className="flex items-center justify-between mt-3 px-3 py-2.5 rounded-xl"
              style={{ backgroundColor: "#EEF2FF" }}
            >
              <span className="text-sm font-bold text-midnight-express">
                Total
              </span>
              <span className="font-mono text-sm font-bold text-medium-slate-blue">
                {fmt(grandTotal)}
              </span>
            </div>

            <Divider />

            {/* ── Payment ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] font-bold uppercase tracking-widest text-echo-blue">
                Payment
              </p>
              <PaymentBadge method={paymentMethod} />
            </div>

            <div className="flex flex-col gap-2">
              {isSplit ? (
                <>
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                      <span>💵</span> Cash
                    </span>
                    <span className="font-mono text-xs font-semibold text-emerald-700">
                      {fmt(cashPaid)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                      <span>💳</span> Card
                    </span>
                    <span className="font-mono text-xs font-semibold text-blue-700">
                      {fmt(cardPaid)}
                    </span>
                  </div>
                </>
              ) : isCard ? (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                    <span>💳</span> Card Payment
                  </span>
                  <span className="font-mono text-xs font-semibold text-blue-700">
                    {fmt(paidAmount)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                    <span>💵</span> Cash Payment
                  </span>
                  <span className="font-mono text-xs font-semibold text-emerald-700">
                    {fmt(paidAmount)}
                  </span>
                </div>
              )}
            </div>

            {hasChange && (
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gulf-blue">Change</span>
                <span className="font-mono text-xs font-semibold text-midnight-express">
                  {fmt(change)}
                </span>
              </div>
            )}

            <div className="mt-5 w-full border-t border-dashed border-echo-blue/30" />
          </div>
        </div>
        {onSendAsInvoice && (
          <div className="flex justify-end border-t border-lavender px-5 py-3">
            <button type="button" className="nl-btn nl-btn--primary" onClick={onSendAsInvoice}>
              Send as invoice
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default connect(
  (state: any) => ({
    selectedShop: state.dashboard.get("selectedShop"),
    currentUser: state.setting.get("currentUser"),
  }),
  {
    createBrand: Actions.product.createBrand,
    updateBrand: Actions.product.updateBrand,
  }
)(InvoiceDetail);
